import { Contract } from '@/DTOS/Contract/Response/Contract';
import { Divider, Drawer, Table } from 'antd';
import moment from 'moment';
import React from 'react';

interface Props {
  handleModalInformationClose: () => void;
  modalInformation: boolean;
  selectedRecord: Contract | null;
  selectedRecordAdditionalAct: Contract | null;
}

const ContractInformation: React.FC<Props> = ({
  handleModalInformationClose,
  modalInformation,
  selectedRecord,
  selectedRecordAdditionalAct,
}) => {
  const isSmallScreen = window.innerWidth <= 768;
  // Helper to compare specific properties between records
  const compareSpecificFields = (oldRecord: Contract | null, newRecord: Contract | null) => {
    const comparisons: any[] = [];

    const fieldsToCompare = [
      { field: 'date', label: 'Date' },
      { field: 'dateStart', label: 'Start Date' },
      { field: 'dateEnd', label: 'End Date' },
    ];

    // Compare dates
    fieldsToCompare.forEach(({ field, label }) => {
      const oldValue = oldRecord ? moment(oldRecord[field as keyof Contract]).format('DD.MM.YYYY') : 'N/A';
      const newValue = newRecord ? moment(newRecord[field as keyof Contract]).format('DD.MM.YYYY') : 'N/A';
      if (oldValue !== newValue) {
        comparisons.push({
          field: label,
          oldValue,
          newValue,
        });
      }
    });

    return comparisons;
  };

  // Process and group `contractWorkPlaceItems`
  const groupContractWorkPlaceItems = (
    record: Contract | null
  ): { workPlace: string; items: { deviceTypeName: string; quantity: number }[] }[] => {
    if (!record || !record.contractWorkPlaceItems) return [];

    // Group items by workPlace
    const groupedItems: Record<string, { deviceTypeName: string; quantity: number }[]> =
      record.contractWorkPlaceItems.reduce((acc: any, item: any) => {
        const workPlaceName = item.workPlace?.name || 'Punct de lucru necunoscut';
        const deviceTypeName = item.device?.name || 'Device necunoscut';
        const quantity = item.piece || 0;

        if (!acc[workPlaceName]) {
          acc[workPlaceName] = [];
        }

        acc[workPlaceName].push({
          deviceTypeName,
          quantity,
        });

        return acc;
      }, {} as Record<string, { deviceTypeName: string; quantity: number }[]>);

    // Convert grouped items to an array of objects
    return Object.entries(groupedItems).map(([workPlace, items]) => ({
      workPlace,
      items,
    }));
  };

  const changes = compareSpecificFields(selectedRecord, selectedRecordAdditionalAct);
  const leftGroupedItems = groupContractWorkPlaceItems(selectedRecord);
  const rightGroupedItems = groupContractWorkPlaceItems(selectedRecordAdditionalAct);

  // Table columns for comparisons
  const changeColumns = [
    {
      title: 'Proprietate',
      dataIndex: 'field',
      key: 'field',
    },
    {
      title: 'Valoare curenta contract',
      dataIndex: 'oldValue',
      key: 'oldValue',
    },
    {
      title: 'Valoare curenta act aditional',
      dataIndex: 'newValue',
      key: 'newValue',
    },
  ];

  // Table columns for grouped items
  const groupedItemsColumns = [
    {
      title: 'Device',
      dataIndex: 'deviceTypeName',
      key: 'deviceTypeName',
    },
    {
      title: 'Cantitate',
      dataIndex: 'quantity',
      key: 'quantity',
    },
  ];

  return (
    <Drawer
      title='Informatii suplimentare'
      placement='right'
      onClose={handleModalInformationClose}
      open={modalInformation}
      width={isSmallScreen ? '95%' : '50%'}
    >
      <h2>Contract Nr. {selectedRecord?.number || 'N/A'} - Schimbari</h2>
      <Divider />

      <Table
        bordered
        pagination={false}
        columns={changeColumns}
        dataSource={changes}
        rowKey={(record) => record.field}
        style={{ marginBottom: '16px' }}
      />

      <Divider />

      <h3>Device-uri</h3>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
        {/* Left Table: selectedRecord */}
        <div style={{ flex: 1 }}>
          <h4>Contractul Acual ({selectedRecord?.contractWorkPlaceItems?.length})</h4>
          {leftGroupedItems.map((group) => (
            <div key={group.workPlace} style={{ marginBottom: '16px' }}>
              <h5>Punct de lucru: {group.workPlace}</h5>
              <Table
                bordered
                pagination={false}
                columns={groupedItemsColumns}
                dataSource={group.items}
                rowKey={(record) => record.deviceTypeName}
              />
            </div>
          ))}
        </div>

        {/* Right Table: selectedRecordAdditionalAct */}
        <div style={{ flex: 1 }}>
          <h4>Act Additional ({selectedRecordAdditionalAct?.contractWorkPlaceItems?.length})</h4>
          {rightGroupedItems.map((group) => (
            <div key={group.workPlace} style={{ marginBottom: '16px' }}>
              <h5>Punct de lucru: {group.workPlace}</h5>
              <Table
                bordered
                pagination={false}
                columns={groupedItemsColumns}
                dataSource={group.items}
                rowKey={(record) => record.deviceTypeName}
              />
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
};

export default ContractInformation;
